import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import Paper from '@base/Paper/Paper';
import fetch from '@api/fetchers';
import { getStartEpochDate } from '@utils/dates';
import { Spacings, mediaQuery } from '@styles/index';
import { useToggleable } from '@stores/toggleables';
import { useSnackbar } from '@stores/snackbar';
import { LOGIN_DRAWER } from '@constants/toggleables';
import segmentStorage from '@events/segmentStorage';
import pushRoute from '@utils/pushRoute';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import {
  PREDICTED_RANK_START_LEARNING_CLICKED,
  PREDICT_NOW_CLICKED,
  RANK_PREDICTION_PAGE_VIEWED
} from '../constant';
import useRankPredictorSegmentAnalytics from '../Common/useRankPredictorSegmentAnalytics';
import Form from './Form';
import Result from './Result';
import {
  getIsIITJeeRankPredictor,
  getExamUIDByGoalUID,
  AITS_EXAM_UID
} from '../utils';

const Wrapper = styled(Paper)`
  height: ${({ $isIITJeeRankPredictor, exam_type }) => {
    return $isIITJeeRankPredictor || exam_type === AITS_EXAM_UID
      ? 'auto'
      : '640px';
  }};

  ${mediaQuery.uptoMobile} {
    height: auto;
    box-shadow: none;
    padding: ${Spacings.SPACING_4B};
    padding-top: ${Spacings.SPACING_9B};
  }
  .hide {
    display: none;
  }
`;

const ExamDetails = ({
  isUnacademyPredictor = false,
  isOfflineRankPredictor = false
}) => {
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { query } = useRouter();
  const { score, userUid, goalUID, exam_type } = query;

  const examUID = getExamUIDByGoalUID(goalUID, exam_type);

  const segmentAnalytics = useRankPredictorSegmentAnalytics();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { open: showSnackbar } = useSnackbar();

  const isIITJeeRankPredictor = getIsIITJeeRankPredictor(examUID);
  const [isRequestedForRankPrediction, setRequestedForRankPrediction] =
    useState(false);
  const [userData, setUserData] = useState(null);

  const formDataRef = useRef({
    score: score ?? null,
    applicationNumber: '',
    dob: ''
  });
  useEffect(() => {
    formDataRef.current.score = score;
  }, [score]);

  const resultDataRef = useRef({
    rank: {
      min: 0,
      max: 0
    },
    percentile: {
      min: 0,
      max: 0
    }
  });

  const [showResult, setShowResult] = useState(false);

  const handleOnPredict = async (data) => {
    if (!isLoggedIn) {
      setRequestedForRankPrediction(true);
      setUserData(data);
      return openLoginDrawer();
    }
    const processedData = {
      score: parseFloat(data.score),
      ...(!isLoggedIn && {
        userUid
      })
    };

    processedData.application_id = data.applicationNumber;
    processedData.dob = getStartEpochDate(data.dob);
    if (data?.slot?.value) {
      processedData.slot = data.slot.value;
    }
    const key =
      isUnacademyPredictor || isOfflineRankPredictor
        ? `v1/exam/${examUID}/predict_rank/?is_offline=true`
        : `v1/exam/${examUID}/predict_rank/`;
    const { error, rank, percentile } = await fetch(key, {
      requestType: 'POST',
      body: processedData
    });

    // Handle situations when predict_rank API errors out.
    const result = error
      ? { rank: { min: 0, max: 0 }, percentile: { min: 0, max: 0 } }
      : { rank, percentile };

    if (error) {
      showSnackbar({
        message: error.message || 'Unable to fetch the rank. Please try again',
        type: 'error'
      });

      return;
    }

    formDataRef.current = data;
    resultDataRef.current = result;

    setShowResult(true);
  };

  useEffect(() => {
    const eventsAnalytics = {};
    if (isUnacademyPredictor || isOfflineRankPredictor) {
      eventsAnalytics.is_offline = true;
    }
    if (isUnacademyPredictor || isOfflineRankPredictor) {
      segmentAnalytics(RANK_PREDICTION_PAGE_VIEWED, eventsAnalytics);
    } else {
      segmentAnalytics(RANK_PREDICTION_PAGE_VIEWED);
    }
    segmentStorage.setItem('lps', `rankpredictor/${goalUID}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoggedIn && isRequestedForRankPrediction && userData !== null) {
      setRequestedForRankPrediction(false);
      handleOnPredict(userData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequestedForRankPrediction, userData, isLoggedIn]);

  const handleOnPredictAgain = () => {
    segmentAnalytics(PREDICT_NOW_CLICKED, {
      total_scored: 'NA',
      correct_answers: 'NA',
      incorrect_answers: 'NA',
      unanswered: 'NA'
    });
    setShowResult(false);
  };

  const handleOnStartLeaning = () => {
    segmentAnalytics(PREDICTED_RANK_START_LEARNING_CLICKED, {
      last_primary_source_section: 'Rank Predictor - Start Leaning CTA'
    });
    // redirecting the user to iit jee home page
    pushRoute(
      '/goal/[goalSlug]/[goalUID]',
      '/goal/jee-main-and-advanced-preparation/TMUVD'
    );
  };
  return (
    <Wrapper
      $isIITJeeRankPredictor={isIITJeeRankPredictor}
      exam_type={exam_type}
    >
      <div className={showResult ? '' : 'hide'}>
        <Result
          rank={resultDataRef.current.rank}
          percentile={resultDataRef.current.percentile}
          score={formDataRef.current.score}
          onPredictAgain={handleOnPredictAgain}
          OnStartLeaning={handleOnStartLeaning}
          isUnacademyPredictor={isUnacademyPredictor}
          isOfflineRankPredictor={isOfflineRankPredictor}
        />
      </div>
      <div className={showResult ? 'hide' : ''}>
        <Form
          initialData={formDataRef.current}
          onPredict={handleOnPredict}
          isUnacademyPredictor={isUnacademyPredictor}
          isOfflineRankPredictor={isOfflineRankPredictor}
        />
      </div>
    </Wrapper>
  );
};

export default ExamDetails;
